/**
 * Recovering a page whose code was deployed away underneath it.
 *
 * Every page after the first is its own webpack chunk, named by a content hash.
 * A deploy replaces them all, so somebody who kept the portal open across one
 * is running an app whose next page no longer exists on the server: the click
 * does nothing and a ChunkLoadError is all that is left of it. PostHog saw 14 in
 * the week to 2026-09-24, on sign-in, invoices, map, users and profile.
 *
 * Reloading at the page they were going to fetches the new build and lands them
 * where they meant to go. It is tried once per destination within a window, so
 * a chunk that is genuinely missing from the new build too cannot put the tab
 * into a reload loop - the second failure is left to surface as the error it is.
 */

const STORAGE_KEY = 'rcChunkReload';
const RETRY_WINDOW_MS = 30 * 1000;

/**
 * Webpack's JS failure is a ChunkLoadError; its CSS failure is a plain Error
 * with code CSS_CHUNK_LOAD_FAILED, which is the one seen on the profile page.
 */
export const isChunkLoadError = (error) => {
  if (!error) {
    return false;
  }
  if (error.name === 'ChunkLoadError' || error.code === 'CSS_CHUNK_LOAD_FAILED') {
    return true;
  }
  const message = String(error.message || '');
  return /Loading (CSS )?chunk [\w-]+ failed/.test(message)
    || /Failed to fetch dynamically imported module/.test(message);
};

const readAttempt = (storage) => {
  try {
    return JSON.parse(storage.getItem(STORAGE_KEY)) || null;
  } catch (e) {
    return null;
  }
};

/**
 * Decides whether to reload for this failure, and records the attempt if so.
 * Storage failing (private mode, blocked) means no guard, so no reload either.
 */
export const shouldReloadForChunk = (error, target, storage, now = Date.now()) => {
  if (!isChunkLoadError(error) || !storage) {
    return false;
  }
  const last = readAttempt(storage);
  if (last && last.target === target && now - last.at < RETRY_WINDOW_MS) {
    return false;
  }
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ target, at: now }));
  } catch (e) {
    return false;
  }
  return true;
};

/** Wires the recovery onto a router. */
export const reloadOnChunkError = (router, win = window) => {
  router.onError((error, to) => {
    const target = to?.fullPath ? router.resolve(to.fullPath).href : win.location.href;
    let storage = null;
    try {
      storage = win.sessionStorage;
    } catch (e) {
      storage = null;
    }
    if (shouldReloadForChunk(error, target, storage)) {
      win.location.assign(target);
    }
  });
};
